<template>
  <div>
    <div class="d-flex align-center">
      <page-title value="各種利用料金"></page-title>
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        color="primary"
        class="ml-4"
        size="24"
      ></v-progress-circular>
      <v-spacer />
      <v-btn
        v-if="invoiceStatus == null"
        @click="updateInvoiceStatus('applied')"
        color="error"
        size="large"
        variant="flat"
        :disabled="isLoading"
      >
        確定
      </v-btn>
      <v-btn
        v-else
        @click="updateInvoiceStatus('canceled')"
        color="error"
        size="large"
        variant="outlined"
        :disabled="invoiceStatus != 'applied' || isLoading"
      >
        確定取り消し
      </v-btn>
    </div>

    <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="4">
            <h5>クラス</h5>
            <v-select
              v-model="searchFilter.classId"
              :items="formattedClasses"
              item-title="displayTitle"
              item-value="classId"
              variant="outlined"
              hide-details
              density="compact"
              clearable
              :disabled="isLoading"
            ></v-select>
          </v-col>

          <v-col cols="4">
            <h5>年度</h5>
            <v-text-field
              v-model.number="searchFilter.year"
              suffix="年度"
              variant="outlined"
              hide-details
              density="compact"
              type="number"
              :disabled="isLoading"
            ></v-text-field>
          </v-col>

          <v-col cols="4">
            <h5>月</h5>
            <v-select
              v-model="searchFilter.month"
              :items="monthItems"
              variant="outlined"
              density="compact"
              :disabled="isLoading"
            ></v-select>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div v-if="invoiceStatus == null && invoiceInfoPeriod.length > 0" class="pa-4">
      <div class="d-flex align-start mb-4">
        <v-icon color="error" class="mr-2 mt-1">mdi-information</v-icon>
        <h4 class="info-header ma-0">
          認定期間が切れている園児がいます。<br />
          <span class="text-body-2 font-weight-regular">認定情報設定より変更後確定を行ってください。</span>
        </h4>
      </div>
      <div class="pl-8 info-body">
        <div class="mb-2">対象園児</div>
        <div v-for="(i, index) in invoiceInfoPeriod" :key="index">・{{ i }}</div>
      </div>
    </div>

    <div v-if="invoiceStatus == null && invoiceInfoAge.length > 0" class="pa-4">
      <div class="d-flex align-start mb-4">
        <v-icon v-if="invoiceInfoPeriod.length === 0" color="error" class="mr-2 mt-1">mdi-information</v-icon>
        <h4 :class="['info-header ma-0', { 'pl-8': invoiceInfoPeriod.length > 0 }]">
          3歳以上の園児が3号認定に設定されています。
        </h4>
      </div>
      <div class="pl-8 info-body">
        <div class="mb-2">対象園児</div>
        <div v-for="(i, index) in invoiceInfoAge" :key="index">・{{ i }}</div>
      </div>
    </div>

    <div class="mt-6">
      <v-table>
        <thead>
          <tr>
            <th class="text-left">氏名</th>
            <th class="text-left">年齢</th>
            <th class="text-left">退園日</th>
            <th class="text-left">支払い方法</th>
            <th class="text-left">階層</th>
            <th class="text-left">保育必要量</th>
            <th class="text-left">保育料（基本料金）</th>
            <th class="text-left">延長料</th>
            <th class="text-left">園提供サービス</th>
            <th class="text-left">合計</th>
            <th class="text-left" v-if="isReadDateCol">開封日</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in invoiceList" :key="item.child.childId">
            <td>{{ item.child.lastName + item.child.firstName }}</td>
            <td>{{ getAgeFromBirthdate(item.child.dateOfBirth) + "歳" }}</td>
            <td>{{ formatDate(item.child.quitDate) }}</td>
            
            <td>{{ getPaymentMethodText(item.invoices[0]?.paymentMethod) }}</td>
            <td>{{ item.invoices[0]?.rank || '-' }}</td>
            <td>
              {{
                item.invoices[0]?.childcareCategory
                  ? childcareCategoryItems.find(
                      (v) => v.value == item.invoices[0].childcareCategory
                    )?.text || ""
                  : ""
              }}
            </td>
            <td>{{ (item.invoices[0]?.nurseryFee || 0) + "円" }}</td>
            <td>{{ (item.invoices[0]?.nurseryAdditionalFee || 0) + "円" }}</td>
            <td>{{ (item.invoices[0]?.nurseryServiceFee || 0) + "円" }}</td>
            <td>
              {{
                (item.invoices[0]?.nurseryFee || 0) +
                (item.invoices[0]?.nurseryAdditionalFee || 0) +
                (item.invoices[0]?.nurseryServiceFee || 0) +
                "円"
              }}
            </td>
            <td v-if="isReadDateCol">
              {{ item.invoices[0]?.readDate ? datetimeConv(item.invoices[0].readDate) : "" }}
            </td>
            <td>
              <v-btn
                @click="clickDetail(item)"
                :disabled="isLoading"
                color="primary"
                variant="flat"
                size="small"
              >詳細</v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import { NurseryClassResponse, InvoiceResponse, NurseryFee } from "chaild-api/lib";
import { invoiceModule, InvoiceStatus } from "@/store/dataModules/invoiceModule";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { paymentMethodItems } from "@/constants/invoice";

dayjs.extend(isSameOrAfter);

export default defineComponent({
  name: "Invoice",

  components: {
    PageTitle,
  },

  data() {
    let year = Number(dayjs().format('YYYY'));
    let month = Number(dayjs().format('M'));
    const date = dayjs().date();
    
    if (date < 15) {
      month -= 1;
    }
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    if (month === 1 || month === 2 || month === 3) {
      year -= 1;
    }

    const invoiceClassId = sessionStorage.getItem('invoiceClassId');
    const invoiceYear = sessionStorage.getItem('invoiceYear');
    const invoiceMonth = sessionStorage.getItem('invoiceMonth');

    return {
      isLoading: false,
      searchFilter: {
        classId: invoiceClassId ? Number(invoiceClassId) : null as null | number,
        year: invoiceYear ? Number(invoiceYear) : year,
        month: invoiceMonth ? Number(invoiceMonth) : month,
      },
      childcareCategoryItems: [
        { text: "標準時間保育", value: "normal" },
        { text: "短時間保育", value: "short" },
      ],
      paymentMethodItems: paymentMethodItems,
    };
  },

  watch: {
    searchFilter: {
      deep: true,
      async handler() {
        if (this.searchFilter.classId) sessionStorage.setItem('invoiceClassId', String(this.searchFilter.classId)); 
        else sessionStorage.setItem('invoiceClassId', '');
        
        if (this.searchFilter.year) sessionStorage.setItem('invoiceYear', String(this.searchFilter.year)); 
        else sessionStorage.setItem('invoiceYear', '');
        
        if (this.searchFilter.month) sessionStorage.setItem('invoiceMonth', String(this.searchFilter.month)); 
        else  sessionStorage.setItem('invoiceMonth', '');
        
        await this.searchInvoice();
        await this.getInvoiceStatus();
        await this.getFee();
      },
    },
  },

  async mounted() {
    // データ初期化は data() に移動したため、ここでは API を叩くだけ (二重通信バグの解消)
    await this.searchInvoice();
    await this.listClass();
    await this.getInvoiceStatus();
    await this.getFee();
  },

  computed: {
    // クラスのドロップダウン表示用
    formattedClasses(): (NurseryClassResponse & { displayTitle: string })[] {
      return this.classes.map(c => ({
        ...c,
        displayTitle: `(${c.year}) ${c.className}`
      }));
    },
    isReadDateCol: function(): boolean {
      const baseDay = dayjs('2000/1/1 00:00:00');
      for (const invoice of invoiceModule.invoiceList) {
        if (invoice.invoices[0]?.readDate && dayjs(invoice.invoices[0].readDate).isSame(baseDay)) {
          return false;
        }
      }
      return true;
    },
    invoiceList: function (): InvoiceResponse[] {
      let filterMonth = "";
      if (this.searchFilter.month < 4) {
        filterMonth = dayjs(`${Number(this.searchFilter.year)+1}/${this.searchFilter.month}`).format("YYYY/MM");
      } else {
        filterMonth = dayjs(`${Number(this.searchFilter.year)}/${this.searchFilter.month}`).format("YYYY/MM");
      }
      return invoiceModule.invoiceList.filter(
        (i) => i.child.quitDate == null || dayjs(i.child.quitDate).isAfter(filterMonth)
      );
    },
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
    monthItems: function (): number[] {
      return [...Array(12).keys()].map((i) => ++i);
    },
    invoiceStatus: function (): InvoiceStatus | null {
      return invoiceModule.status;
    },
    invoiceInfoPeriod: function (): string[] {
      const FeeChild: string[] = [];
      for (const invoice of this.invoiceList ) {
        for (const fee of invoiceModule.feeList) {
          if (invoice.child.childId == fee.child.childId) {
            if (fee.fees.length > 0) {
              const feeChild = this.validateFeePeriod(fee.fees);
              if (feeChild) {
                FeeChild.push(`${fee.child.lastName}${fee.child.firstName}`);
              }
            }
          }
        }
      }
      return FeeChild;
    },
    invoiceInfoAge: function (): string[] {
      const FeeChild: string[] = [];
      for (const invoice of this.invoiceList) {
        for (const fee of invoiceModule.feeList) {
          if (invoice.child.childId == fee.child.childId) {
            const age = this.getAgeFromBirthdate(fee.child.dateOfBirth);
            if (age < 3) break;
            
            if (fee.fees.length > 0) {
              const feeChild = this.validateFeePeriod(fee.fees, age);
              if (feeChild) {
                FeeChild.push(`${fee.child.lastName}${fee.child.firstName}`);
              }
            }
          }
        }
      }
      return FeeChild;
    },
  },

  methods: {
    datetimeConv(strDatetime: string) {
      return dayjs(strDatetime).format('YYYY-MM-DD HH:mm');
    },
    async searchInvoice() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await invoiceModule.listInvoice({
          nurseryId,
          classId: this.searchFilter.classId || undefined,
          year: Number(this.searchFilter.year) || undefined,
          month: Number(this.searchFilter.month) || undefined,
        });
      }
      this.isLoading = false;
    },
    async listClass() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({ nurseryId });
      }
      this.isLoading = false;
    },
    async getInvoiceStatus() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await invoiceModule.getInvoiceStatus({
          nurseryId,
          year: Number(this.searchFilter.year),
          month: Number(this.searchFilter.month),
        });
      }
      this.isLoading = false;
    },
    async updateInvoiceStatus(status: "applied" | "canceled") {
      this.isLoading = true;
      const confirmMsg = status === "applied" 
        ? `${this.searchFilter.year}年${this.searchFilter.month}月分を確定しますか？`
        : `${this.searchFilter.year}年${this.searchFilter.month}月分の確定を取り消しますか？`;

      if (confirm(confirmMsg)) {
        const nurseryId = LocalDataService.getNurseryId();
        if (nurseryId) {
          const response = await invoiceModule.updateInvoiceStatus({
            nurseryId,
            year: Number(this.searchFilter.year),
            month: Number(this.searchFilter.month),
            status: status,
          }).catch(() => {
            alert(status === "applied" 
              ? "確定できませんでした。既に運営側で売上が確定しているか確認してください。またサービス設定および保育料等の設定を行ってください"
              : "確定取り消しできませんでした。サービス設定および保育料等の設定を行ってください"
            );
          });
          if (response) {
            await this.getInvoiceStatus();
          }
        }
      }
      this.isLoading = false;
    },
    async getFee() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await invoiceModule.getFee({
          nurseryId,
          classId: this.searchFilter.classId ? String(this.searchFilter.classId) : undefined,
          limit: 1000,
          skip: 0,
        });
      }
      this.isLoading = false;
    },
    getAgeFromBirthdate(date: string): number {
      return dayjs().diff(dayjs(date), "year");
    },
    clickDetail(invoice: InvoiceResponse) {
      this.$router.push({
        path: "/invoice/detail",
        query: {
          year: String(this.searchFilter.year),
          month: String(this.searchFilter.month),
          childId: String(invoice.child.childId),
        },
      });
    },
    formatDate(value: string | null | undefined): string {
      if (!value) return "-";
      return dayjs(value).format("YYYY/MM/DD");
    },
    getPaymentMethodText(value?: string): string {
      if (!value) return "集金";
      return this.paymentMethodItems.find((p) => p.value == value)?.text || "集金";
    },
    validateFeePeriod(nurseryFee: NurseryFee[], age?: number): NurseryFee | null {
      const fees: NurseryFee[] = nurseryFee.slice().reverse();
      const date = dayjs().format('YYYY-MM-DD');
      let Fee: NurseryFee | null = null;
      let ageFee: NurseryFee | null = null;
      let end: NurseryFee | null = null;
      let cnt = 0;
      for (const fee of fees) {
        if (cnt == 0 && dayjs(date).isBefore(fee.periodStart)) break;
        if (end) {
          if (fee.periodEnd) {
            if (dayjs(date).isAfter(fee.periodEnd)) {
              end = fee;
              cnt += 1;
              continue;
            } else if (dayjs(date).isSameOrAfter(fee.periodStart)) {
              end = null;
            }
          } else {
            if (dayjs(date).isSameOrAfter(fee.periodStart)) end = null;
            else if (dayjs(date).isBefore(fee.periodStart)) {
              Fee = end;
              end = null;
            }
          }
        } else {
          if (fee.periodEnd && dayjs(date).isAfter(fee.periodEnd)) {
            end = fee;
            cnt += 1;
            continue;
          }
        }
        if (age) ageFee = fee;
        break;
      }
      if (end != null && cnt > 0) {
        Fee = end;
        ageFee = end;
      }
      if (age) {
        if (Fee && Fee.certifiedCategory == 'three') return Fee;
        else if (ageFee && ageFee.certifiedCategory == 'three') return ageFee;
        else return null;
      }
      return Fee;
    }
  },
});
</script>

<style lang="scss" scoped>
.info-header {
  color: #B00020; /* Vuetify の error カラーに近い色に変更 */
}

.info-body {
  color: #B00020;
}
</style>